import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Typography } from "@material-tailwind/react";
import {
  FaDownload,
  FaFileAlt,
  FaFileArchive,
  FaFilePdf,
  FaFileWord,
} from "react-icons/fa";
import { useTranslation } from "react-i18next";
import { ASSET_URI, PROXY, getCache, setCache } from "../lib/utils";
import { getLanguageDirection } from "../i18n/config";

const getFileIcon = (filePath) => {
  const ext = (filePath || "").split(".").pop()?.toLowerCase();
  if (ext === "pdf") return <FaFilePdf className="text-red-600" size={14} />;
  if (ext === "docx") return <FaFileWord className="text-blue-600" size={14} />;
  if (ext === "zip") {
    return <FaFileArchive className="text-amber-600" size={14} />;
  }
  return <FaFileAlt className="text-gray-500" size={14} />;
};

const slugify = (value = "") =>
  value
    .toString()
    .trim()
    .toLowerCase()
    .replace(/[^\p{L}\p{N}\s-]/gu, "")
    .replace(/\s+/g, "-");

const normalizeGuidelineAssetUrls = (html = "") =>
  html.replace(
    /(<img[^>]*src=["'])([^"']+)(["'][^>]*>)/gi,
    (match, prefix, src, suffix) => {
      const trimmedSrc = src?.trim();
      const uploadsMatch = trimmedSrc?.match(
        /(?:https?:\/\/[^/]+)?\/?(uploads\/.+)$/i,
      );

      if (!uploadsMatch?.[1]) {
        return match;
      }

      return `${prefix}${ASSET_URI}/${uploadsMatch[1].replace(
        /^\/+/,
        "",
      )}${suffix}`;
    },
  );

const Guidance = () => {
  const { t, i18n } = useTranslation();
  const [standards, setStandards] = useState([]);
  const [standardsLoading, setStandardsLoading] = useState(false);
  const [standardsError, setStandardsError] = useState("");
  const [guideline, setGuideline] = useState(null);
  const [guidelineLoading, setGuidelineLoading] = useState(false);
  const [guidelineError, setGuidelineError] = useState("");
  const [activeSection, setActiveSection] = useState("");

  const currentLanguage = ["en", "pr", "ps"].includes(i18n.language)
    ? i18n.language
    : "en";
  const isRTL = getLanguageDirection(currentLanguage) === "rtl";

  const normalizeGuideline = useCallback(
    (payload) => {
      const locale =
        payload?.locales?.[currentLanguage] || payload?.locales?.en || {};
      const fallbackLocale = payload?.locales?.en || {};

      return {
        title: locale.title || fallbackLocale.title || t("attachments.title"),
        subtitle:
          locale.subtitle ||
          fallbackLocale.subtitle ||
          t("attachments.subtitle"),
        sidebarHeadings:
          locale.sidebarHeadings?.length > 0
            ? locale.sidebarHeadings
            : fallbackLocale.sidebarHeadings || [],
        content: normalizeGuidelineAssetUrls(
          locale.content || fallbackLocale.content || "",
        ),
      };
    },
    [currentLanguage, t],
  );

  useEffect(() => {
    const cacheKey = "video-standards";
    const cached = getCache(cacheKey);

    if (cached) {
      setStandards(cached);
      return;
    }

    const fetchStandards = async () => {
      try {
        setStandardsLoading(true);
        setStandardsError("");
        const response = await fetch(`${PROXY}/standards`);
        if (!response.ok) {
          throw new Error(t("Something-went-wrong."));
        }
        const data = await response.json();
        const results = data?.results || [];
        setStandards(results);
        setCache(cacheKey, results, 6);
      } catch (err) {
        setStandardsError(err.message || t("Something-went-wrong."));
        setStandards([]);
      } finally {
        setStandardsLoading(false);
      }
    };

    fetchStandards();
  }, [t]);

  useEffect(() => {
    const fetchGuideline = async () => {
      try {
        setGuidelineLoading(true);
        setGuidelineError("");
        const response = await fetch(
          `${PROXY}/guideline?lang=${currentLanguage}&t=${Date.now()}`,
        );
        if (!response.ok) {
          throw new Error(t("Something-went-wrong."));
        }
        const data = await response.json();
        setGuideline(normalizeGuideline(data?.guideline));
      } catch (err) {
        setGuidelineError(err.message || t("Something-went-wrong."));
        setGuideline(null);
      } finally {
        setGuidelineLoading(false);
      }
    };

    fetchGuideline();
  }, [currentLanguage, normalizeGuideline, t]);

  const documentData = useMemo(() => {
    if (!guideline?.content || typeof window === "undefined") {
      return {
        html: guideline?.content || "",
        sections:
          guideline?.sidebarHeadings?.map((heading, index) => ({
            id: `section-${index + 1}`,
            label: heading,
          })) || [],
      };
    }

    const parser = new window.DOMParser();
    const doc = parser.parseFromString(guideline.content, "text/html");
    const headings = Array.from(doc.body.querySelectorAll("h1, h2, h3"));
    const sections = [];
    const usedIds = new Set();

    const createUniqueId = (value, fallbackIndex) => {
      const baseId = slugify(value) || `section-${fallbackIndex}`;
      let candidate = baseId;
      let counter = 1;

      while (usedIds.has(candidate)) {
        candidate = `${baseId}-${counter}`;
        counter += 1;
      }

      usedIds.add(candidate);
      return candidate;
    };

    headings.forEach((heading, index) => {
      const label =
        guideline.sidebarHeadings?.[index] ||
        heading.textContent?.trim() ||
        `Section ${index + 1}`;
      const id = createUniqueId(label, index + 1);
      heading.setAttribute("id", id);
      sections.push({ id, label });
    });

    if (sections.length === 0 && guideline.sidebarHeadings?.length) {
      const introId = "guideline-overview";
      sections.push(
        ...guideline.sidebarHeadings.map((heading, index) => ({
          id: index === 0 ? introId : `guideline-overview-${index + 1}`,
          label: heading,
        })),
      );
    }

    return {
      html: doc.body.innerHTML,
      sections,
    };
  }, [guideline]);

  const pageIntroTitle = guideline?.title || t("attachments.title");
  const pageIntroSubtitle = guideline?.subtitle || t("attachments.subtitle");
  const hasGuidelineContent = Boolean(guideline?.content);
  const documentSections = useMemo(
    () => [
      ...(documentData.sections || []),
      { id: "attachments-downloads", label: t("attachments.downloads-title") },
    ],
    [documentData.sections, t],
  );

  useEffect(() => {
    if (!documentSections.length) {
      setActiveSection("");
      return;
    }

    const elements = documentSections
      .map((section) => document.getElementById(section.id))
      .filter(Boolean);

    if (!elements.length) {
      setActiveSection(documentSections[0]?.id || "");
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        const visibleEntry = entries
          .filter((entry) => entry.isIntersecting)
          .sort(
            (a, b) => a.boundingClientRect.top - b.boundingClientRect.top,
          )[0];

        if (visibleEntry?.target?.id) {
          setActiveSection(visibleEntry.target.id);
        }
      },
      {
        rootMargin: "-18% 0px -65% 0px",
        threshold: [0.1, 0.3, 0.6],
      },
    );

    elements.forEach((element) => observer.observe(element));

    if (!activeSection && documentSections[0]?.id) {
      setActiveSection(documentSections[0].id);
    }

    return () => observer.disconnect();
  }, [activeSection, documentSections]);

  return (
    <section
      className="container mx-auto px-4 py-10 space-y-6"
      dir={isRTL ? "rtl" : "ltr"}
    >
      <div className="grid grid-cols-1 gap-6 xl:grid-cols-[280px_minmax(0,1fr)]">
        <aside className="rounded-2xl border border-gray-200 bg-white p-5 shadow-sm xl:sticky xl:top-24 xl:h-fit">
          <Typography variant="h6" className="mb-4 font-semibold text-gray-900">
            {pageIntroTitle}
          </Typography>
          {guidelineLoading ? (
            <p className="text-sm text-gray-500">{t("loading")}</p>
          ) : guidelineError ? (
            <p className="text-sm text-red-500">{guidelineError}</p>
          ) : documentSections.length ? (
            <nav className="space-y-2">
              {documentSections.map((section, index) => {
                const isActive = activeSection === section.id;

                return (
                  <a
                    key={`${section.id}-${index}`}
                    href={`#${section.id}`}
                    className={`block rounded-xl border px-4 py-3 text-sm transition ${
                      isActive
                        ? "border-indigo-200 bg-indigo-50 font-semibold text-indigo-700"
                        : "border-transparent bg-white text-gray-700 hover:border-gray-200 hover:bg-gray-50"
                    }`}
                  >
                    {section.label}
                  </a>
                );
              })}
            </nav>
          ) : (
            <p className="text-sm text-gray-500">
              {t("no-standards-available")}
            </p>
          )}
        </aside>

        <div className="space-y-6">
          <article className="rounded-2xl border border-gray-200 bg-white p-6 shadow-sm md:p-8">
            <div className="space-y-8">
              <section className="rounded-2xl border border-indigo-100 bg-indigo-50/60 p-5">
                <Typography
                  variant="h6"
                  className="font-semibold text-indigo-900"
                >
                  {pageIntroTitle}
                </Typography>
                <Typography className="mt-2 text-sm leading-6 text-indigo-800/80">
                  {pageIntroSubtitle}
                </Typography>
              </section>

              <section className="space-y-5">
                <div className="border-b border-gray-100 pb-4">
                  <p className="mb-2 text-xs font-semibold uppercase tracking-[0.2em] text-gray-500">
                    Guideline Content
                  </p>
                  <Typography
                    variant="h5"
                    className="font-semibold text-gray-900"
                  >
                    {guideline?.title || pageIntroTitle}
                  </Typography>
                  {guideline?.subtitle ? (
                    <Typography className="mt-2 text-sm leading-6 text-gray-500">
                      {guideline.subtitle}
                    </Typography>
                  ) : null}
                </div>

                {guidelineLoading ? (
                  <p className="text-sm text-gray-500">{t("loading")}</p>
                ) : guidelineError ? (
                  <p className="text-sm text-red-500">{guidelineError}</p>
                ) : hasGuidelineContent ? (
                  <div
                    className={`max-w-none text-[15px] leading-8 text-gray-700 [&_a]:text-indigo-700 [&_a]:underline [&_blockquote]:my-5 [&_blockquote]:rounded-2xl [&_blockquote]:border-s-4 [&_blockquote]:border-indigo-400 [&_blockquote]:bg-indigo-50 [&_blockquote]:px-5 [&_blockquote]:py-4 [&_h1]:mb-4 [&_h1]:text-3xl [&_h1]:font-bold [&_h1]:text-gray-900 [&_h2]:mb-4 [&_h2]:mt-10 [&_h2]:scroll-mt-28 [&_h2]:text-2xl [&_h2]:font-semibold [&_h2]:text-gray-900 [&_h3]:mb-3 [&_h3]:mt-8 [&_h3]:scroll-mt-28 [&_h3]:text-xl [&_h3]:font-semibold [&_h3]:text-gray-900 [&_img]:my-6 [&_img]:max-w-full [&_img]:rounded-2xl [&_img]:border [&_img]:border-gray-200 [&_img]:shadow-sm [&_ol]:my-5 [&_ol]:list-decimal [&_p]:mb-4 [&_table]:my-6 [&_table]:w-full [&_table]:border-collapse [&_td]:border [&_td]:border-gray-200 [&_td]:p-3 [&_th]:border [&_th]:border-gray-200 [&_th]:bg-gray-50 [&_th]:p-3 [&_ul]:my-5 [&_ul]:list-disc ${
                      isRTL
                        ? "[&_ol]:pr-6 [&_ul]:pr-6"
                        : "[&_ol]:pl-6 [&_ul]:pl-6"
                    }`}
                    dangerouslySetInnerHTML={{ __html: documentData.html }}
                  />
                ) : (
                  <div className="rounded-2xl border border-dashed border-gray-300 bg-gray-50 p-5">
                    <p className="text-sm text-gray-500">
                      {t("no-standards-available")}
                    </p>
                  </div>
                )}
              </section>
            </div>
          </article>

          <div
            id="attachments-downloads"
            className="rounded-2xl border border-gray-200 bg-white p-6 shadow-sm space-y-4"
          >
            <div>
              <Typography variant="h6" className="font-semibold text-gray-900">
                {t("attachments.downloads-title")}
              </Typography>
              <Typography className="mt-1 text-sm text-gray-500">
                {t("attachments.downloads-description")}
              </Typography>
            </div>
            <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
              {standardsLoading ? (
                <p className="text-sm text-gray-500">{t("loading")}</p>
              ) : standardsError ? (
                <p className="text-sm text-red-500">{standardsError}</p>
              ) : standards.length > 0 ? (
                standards.map((file) => (
                  <a
                    key={file._id}
                    href={`${ASSET_URI}/${file.filePath}`}
                    download
                    className="flex items-center justify-between gap-3 rounded-2xl border border-indigo-100 bg-indigo-50/60 px-4 py-4 text-sm font-medium text-indigo-700 transition hover:bg-indigo-500 hover:text-white"
                  >
                    <div className="flex min-w-0 items-center gap-2">
                      {getFileIcon(file.filePath)}
                      <span className="truncate">
                        {file.title ||
                          file.originalName ||
                          t("video-standards")}
                      </span>
                    </div>
                    <FaDownload size={14} />
                  </a>
                ))
              ) : (
                <p className="text-sm text-gray-500">
                  {t("no-standards-available")}
                </p>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Guidance;
